import { FormEvent, useState } from "react";
import { Button, Form } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../Hooks/axiosApi";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";

type LenderForm = {
  lenderName: string;
  initial: string;
  firstName: string;
  surname: string;
  email: string;
  mobile: string;
};

const defaultForm: LenderForm = {
  lenderName: "",
  initial: "",
  firstName: "",
  surname: "",
  email: "",
  mobile: "",
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const auMobileRegex = /^04\d{8}$/;

const normalizeAustralianMobile = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.startsWith("61")) {
    return `0${digits.slice(2)}`.slice(0, 10);
  }
  return digits.slice(0, 10);
};

// Format raw digits as XXXX XXX XXX for display
const formatMobileDisplay = (digits: string) => {
  const d = digits.replace(/\D/g, "");
  if (d.length <= 4) return d;
  if (d.length <= 7) return `${d.slice(0, 4)} ${d.slice(4)}`;
  return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7, 10)}`;
};

const AddCRMLender = () => {
  const navigate = useSafeNavigate();
  const [form, setForm] = useState<LenderForm>(defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof LenderForm, string>>>({});
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string>("");

  const handleChange = (name: keyof LenderForm, value: string) => {
    let nextValue = value;

    if (name === "mobile") {
      nextValue = normalizeAustralianMobile(value);
    }
    if (name === "initial") {
      nextValue = value.replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 3);
    }
    if (name === "lenderName") {
      nextValue = value.slice(0, 100);
    }
    if (name === "firstName" || name === "surname") {
      nextValue = value.slice(0, 50);
    }
    if (name === "email") {
      nextValue = value.slice(0, 100);
    }

    setForm((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setApiError("");
  };

  const validate = () => {
    const nextErrors: Partial<Record<keyof LenderForm, string>> = {};
    const lenderName = form.lenderName.trim();
    const email = form.email.trim();

    if (!lenderName) {
      nextErrors.lenderName = "Lender Name is required.";
    }

    if (email && !emailRegex.test(email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (form.mobile && !auMobileRegex.test(form.mobile)) {
      nextErrors.mobile = "Enter valid Australian mobile (04XXXXXXXX).";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validate()) {
      toast.error("Please correct validation errors.");
      return;
    }

    setSubmitting(true);
    setApiError("");

    const payload = {
      lenderName: form.lenderName.trim(),
      initial: form.initial.trim(),
      firstName: form.firstName.trim(),
      surname: form.surname.trim(),
      email: form.email.trim(),
      mobile: form.mobile.trim(),
      rankNumber: 0,
    };

    try {
      await api.post("/api/Lenders/Postlenders", payload);
      toast.success("Lender added successfully.");
      navigate("/CRMLenders");
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to add lender.";
      setApiError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="px-4 py-4"
      style={{
        minHeight: "300px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
      }}
    >
      <Form
        onSubmit={handleSubmit}
        style={{
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "20px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="text-primary f-w-900 m-0">Add Lender</h3>
        </div>

        {!!apiError && (
          <div className="alert alert-danger py-2" role="alert">
            {apiError}
          </div>
        )}

        {/* Lender Name */}
        <div className="row align-items-center mb-2">
          <div className="col-sm-3">
            <label className="form-label mb-0">Lender Name</label>
          </div>
          <div className="col-sm-3">
            <input
              className={`form-control ${errors.lenderName ? "is-invalid" : ""}`}
              type="text"
              value={form.lenderName}
              maxLength={100}
              onChange={(e) => handleChange("lenderName", e.target.value)}
            />
            {errors.lenderName && <div className="invalid-feedback">{errors.lenderName}</div>}
          </div>
        </div>

        {/* Initial */}
        <div className="row align-items-center mb-2">
          <div className="col-sm-3">
            <label className="form-label mb-0">Initial</label>
          </div>
          <div className="col-sm-3">
            <input
              className="form-control"
              type="text"
              value={form.initial}
              maxLength={3}
              onChange={(e) => handleChange("initial", e.target.value)}
              style={{ maxWidth: "80px" }}
            />
          </div>
        </div>

        {/* First Name */}
        <div className="row align-items-center mb-2">
          <div className="col-sm-3">
            <label className="form-label mb-0">First Name</label>
          </div>
          <div className="col-sm-3">
            <input
              className="form-control"
              type="text"
              value={form.firstName}
              maxLength={50}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </div>
        </div>

        {/* Surname */}
        <div className="row align-items-center mb-2">
          <div className="col-sm-3">
            <label className="form-label mb-0">Surname</label>
          </div>
          <div className="col-sm-3">
            <input
              className="form-control"
              type="text"
              value={form.surname}
              maxLength={50}
              onChange={(e) => handleChange("surname", e.target.value)}
            />
          </div>
        </div>

        {/* Email */}
        <div className="row align-items-center mb-2">
          <div className="col-sm-3">
            <label className="form-label mb-0">Email</label>
          </div>
          <div className="col-sm-3">
            <input
              className={`form-control ${errors.email ? "is-invalid" : ""}`}
              type="email"
              value={form.email}
              maxLength={100}
              onChange={(e) => handleChange("email", e.target.value)}
            />
            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
          </div>
        </div>

        {/* Mobile */}
        <div className="row align-items-center mb-2">
          <div className="col-sm-3">
            <label className="form-label mb-0">Mobile</label>
          </div>
          <div className="col-sm-3">
            <input
              className={`form-control ${errors.mobile ? "is-invalid" : ""}`}
              type="tel"
              inputMode="tel"
              value={formatMobileDisplay(form.mobile)}
              onChange={(e) => handleChange("mobile", e.target.value)}
              maxLength={12}
              placeholder="04XX XXX XXX"
            />
            {errors.mobile && <div className="invalid-feedback">{errors.mobile}</div>}
          </div>
        </div>

        <div className="d-flex gap-2 mt-4">
          <Button type="button" color="secondary" onClick={() => navigate("/CRMLenders")}>
            Back to Lenders
          </Button>
          <Button type="submit" color="primary" disabled={submitting}>
            {submitting ? "Saving..." : "Save Lender"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddCRMLender;
